// src/components/tokenPage/overview/KeyStatsCard.tsx

import React, { useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { KpiBox } from "../shared/TokenDashboardComponents";
import { useQuery } from "@tanstack/react-query";
import { fetchTokensChanges, fetchTokensRollups } from "@/services/queries/tokens/tokensScreenerQueries";
import { formatLargeNumberCell, formatPercentage } from "@/utils/formatters";
import { DataCard } from "@/components/ui";
import { useTokenFundamentals } from "@/contexts/token";

type KeyStatsWindow = '1d' | '7d' | '30d' | '365d';

/**
 * KeyStatsCard - displays current snapshot values with period changes
 * 
 * Uses TokenFundamentalsContext for:
 * - snapshot values (marketCap, volume, tvl, fees, revenue)
 * - current ratios (mcToTvl, mcToFees, feesToTx)
 * 
 * Still queries for:
 * - Period changes (different aggregation endpoint)
 * - Period rollups (different aggregation endpoint)
 */
const KeyStatsCard: React.FC = () => {
  const [keyStatsWindow, setKeyStatsWindow] = useState<KeyStatsWindow>('1d');
  
  // Get snapshot values and ratios from context
  const { ticker, snapshot, current, isLoading: fundamentalsLoading } = useTokenFundamentals();
  const { marketCap, volume, tvl, fees, revenue } = snapshot;
  const { mcToTvl: ratioMcTvl, mcToFees: ratioMcFees, feesToTx: ratioFeesToTxn } = current;

  // Fetch period changes (these are windowed aggregations, not in context)
  const changesQ = useQuery({
    queryKey: ["token", "changes", ticker, keyStatsWindow],
    queryFn: ({ signal }) => fetchTokensChanges({ 
      tickersCsv: ticker, 
      metricKeysCsv: "market_cap,volume,tvl,fees,revenue", 
      windowsCsv: keyStatsWindow, 
      signal 
    }),
    staleTime: 60_000,
    enabled: !!ticker,
  });
  
  const tokenChanges = changesQ.data?.tokens?.[0];
  const getChange = (metricKey: string) => {
    const metric = tokenChanges?.metrics?.find(m => m.metric_key === metricKey);
    const value = metric?.windows?.[keyStatsWindow] as { value?: number | null } | undefined;
    return value?.value ?? null;
  };
  const mcChange = getChange("market_cap");
  const volumeChange = getChange("volume");
  const tvlChange = getChange("tvl");
  const feesChange = getChange("fees");
  const revenueChange = getChange("revenue");

  // Fetch period rollups (windowed sums, not in context)
  const rollupsQ = useQuery({
    queryKey: ["token", "rollups", ticker, keyStatsWindow],
    queryFn: ({ signal }) => fetchTokensRollups({ 
      tickersCsv: ticker, 
      metricKeysCsv: "volume,fees,revenue", 
      windowsCsv: keyStatsWindow, 
      signal 
    }),
    staleTime: 60_000,
    enabled: !!ticker,
  });
  
  const tokenRollups = rollupsQ.data?.tokens?.[0];
  const getRollup = (metricKey: string) => {
    const metric = tokenRollups?.metrics?.find(m => m.metric_key === metricKey);
    const w = metric?.windows?.[keyStatsWindow] as { value?: number | null } | undefined;
    return w?.value ?? null;
  };
  const volumeRoll = getRollup("volume");
  const feesRoll = getRollup("fees");
  const revenueRoll = getRollup("revenue");

  const windowLabel = keyStatsWindow === '1d' ? '24h' : keyStatsWindow;
  const isLoading = fundamentalsLoading || changesQ.isLoading || rollupsQ.isLoading;
  
  const formatFeesToTxn = (value: number | null) => value == null ? "N/A" : `$${value.toFixed(4)}`;

  // Hide if no data
  const hasData = marketCap != null || volume != null || tvl != null || fees != null || revenue != null;
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Key Stats"
      subtitle="Valuation, activity and efficiency at a glance"
      icon="bar-chart-3"
      iconColor="text-blue-400"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={
        <TimeRangePicker options={['1d', '7d', '30d', '365d'] as const} value={keyStatsWindow} onChange={setKeyStatsWindow} theme="blue" />
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {marketCap != null && (
          mcChange != null ? (
            <KpiBox label="Market Cap" value={formatLargeNumberCell(marketCap, 2, "$")} sub={formatPercentage(mcChange * 100)} subToneBySign />
          ) : (
            <KpiBox label="Market Cap" value={formatLargeNumberCell(marketCap, 2, "$")} />
          )
        )}
        {volumeRoll != null || volume != null ? (
          volumeChange != null ? (
            <KpiBox label={`Volume (${windowLabel})`} value={formatLargeNumberCell(volumeRoll ?? volume ?? 0, 2, "$")} sub={formatPercentage(volumeChange * 100)} subToneBySign />
          ) : (
            <KpiBox label={`Volume (${windowLabel})`} value={formatLargeNumberCell(volumeRoll ?? volume ?? 0, 2, "$")} />
          )
        ) : null}
        {tvl != null && (
          tvlChange != null ? (
            <KpiBox label="TVL" value={formatLargeNumberCell(tvl, 2, "$")} sub={formatPercentage(tvlChange * 100)} subToneBySign />
          ) : (
            <KpiBox label="TVL" value={formatLargeNumberCell(tvl, 2, "$")} />
          )
        )}
        {feesRoll != null || fees != null ? (
          feesChange != null ? (
            <KpiBox label={`Fees (${windowLabel})`} value={formatLargeNumberCell(feesRoll ?? fees ?? 0, 2, "$")} sub={formatPercentage(feesChange * 100)} subToneBySign />
          ) : (
            <KpiBox label={`Fees (${windowLabel})`} value={formatLargeNumberCell(feesRoll ?? fees ?? 0, 2, "$")} />
          )
        ) : null}
        {revenueRoll != null || revenue != null ? (
          revenueChange != null ? (
            <KpiBox label={`Revenue (${windowLabel})`} value={formatLargeNumberCell(revenueRoll ?? revenue ?? 0, 2, "$")} sub={formatPercentage(revenueChange * 100)} subToneBySign />
          ) : (
            <KpiBox label={`Revenue (${windowLabel})`} value={formatLargeNumberCell(revenueRoll ?? revenue ?? 0, 2, "$")} />
          )
        ) : null}
        {ratioMcTvl != null && (<KpiBox label="MC / TVL" value={String(Number(ratioMcTvl).toFixed(2))} />)}
        {ratioMcFees != null && (<KpiBox label="MC / Fees" value={String(Number(ratioMcFees).toFixed(2))} />)}
        {ratioFeesToTxn != null && (<KpiBox label="Fees / Txn" value={formatFeesToTxn(ratioFeesToTxn)} />)}
      </div>
    </DataCard>
  );
};

export default KeyStatsCard;
